import React from 'react';
import { Field } from 'react-final-form';
import { isEmpty } from 'lodash';
import { Column, Flex, Input, Box, Checkbox } from '../../design-system';
import { useEffectOnce } from 'react-use';
import { createSelectOptions, errorMsg } from '../../helpers/utils';
import FieldColumn from '../../components/FieldColumn';
import ReadOnlyColumn from '../../components/ReadOnlyColumn';
import { PROJECT_FIELDS } from './constants';
import { useData } from '../../helpers/data-loader';
import DateField from '../../components/DateField';

const createModifiedReadOnlyField = (field: any, name: string) => ({
  ...field,
  name,
  transformOnBlur: (val) => val.name,
});

interface GeneralInformationFieldsProps {
  project: any;
  canEdit: boolean;
  isEditing: boolean;
  user: any;
  handleChangeInStatus: (status: any) => void;
}

const GeneralInformationFields = ({
  project,
  canEdit,
  isEditing,
  user: { members, agency },
  handleChangeInStatus,
}: GeneralInformationFieldsProps) => {
  const [funderData, fetchFunderData] = useData('/funder', {
    errorMsg: errorMsg('funders'),
  });
  const [clientData, fetchClientData] = useData('/client', {
    errorMsg: errorMsg('clients'),
  });

  useEffectOnce(() => {
    fetchFunderData();
    fetchClientData();
  });

  return PROJECT_FIELDS.generalInfo.map((field) => {
    let modifiedReadOnlyField;
    if (field.name === 'project_lead') {
      modifiedReadOnlyField = createModifiedReadOnlyField(field, 'projectLead');
    }
    if (field.name === 'funded_program') {
      modifiedReadOnlyField = {
        ...field,
        name: 'funded_program',
        transformOnBlur: ({ programs }) => {
          if (
            !isEmpty(programs) &&
            project.funded_program &&
            programs.find(({ id }) => id === project.funded_program)
          ) {
            return programs.find(({ id }) => id === project.funded_program)
              .name;
          }
        },
      };
    }
    if (field.name === 'client.name') {
      modifiedReadOnlyField = createModifiedReadOnlyField(
        { ...field, value: project.client_id },
        'client',
      );
    }
    if (field.name === 'funder.name') {
      modifiedReadOnlyField = createModifiedReadOnlyField(
        { ...field, value: project.funder_id },
        'funder',
      );
    }
    if (canEdit) {
      let modifiedEditField;
      if (field.name === 'project_lead') {
        modifiedEditField = {
          ...field,
          options: createSelectOptions(members),
        };
      }
      if (field.name === 'funded_program') {
        if (agency?.plan_id === 1)
          return <Column key={field.name} width={300} />;
        modifiedEditField = {
          ...field,
          options:
            project.client && createSelectOptions(project.client.programs),
          placeholder:
            project.client &&
            project.client.programs.length < 1 &&
            'Add programs in the Vault',
        };
      }
      if (field.name === 'status') {
        modifiedEditField = {
          ...field,
          handleChangeInStatus,
        };
      }
      if (!isEditing) {
        return (
          <ReadOnlyColumn
            key={field.name}
            data={project}
            width={(field.width || 300) as any}
            field={modifiedReadOnlyField || field}
          />
        );
      }
      if (field.name !== 'deadline' && field.type === 'date') {
        return (
          <Column key={field.name} pb={2} width={300}>
            <Field name={field.name}>
              {({ input }) => {
                return (
                  <DateField
                    name={field.name}
                    label={field.label}
                    selected={input.value}
                    onChange={(value) => input.onChange(value)}
                    showPastDue={false}
                  />
                );
              }}
            </Field>
          </Column>
        );
      }

      if (field.name === 'deadline' && isEditing) {
        return (
          <Column key={field.name} pb={2} width={300}>
            <Flex flexDirection="column">
              <Box pb={2}>
                <Field name={field.name}>
                  {({ input }) => {
                    return (
                      <DateField
                        name={field.name}
                        label="Deadline"
                        selected={input.value}
                        onChange={(value) => input.onChange(value)}
                      />
                    );
                  }}
                </Field>
              </Box>
              <Box width={150} height={30}>
                <Field name="hard_deadline">
                  {({ input }) => (
                    <Checkbox
                      {...input}
                      id="projectEditHardDeadline"
                      name="hard_deadline"
                      label="Hard Deadline"
                      onChange={(__, value) => input.onChange(value)}
                    />
                  )}
                </Field>
              </Box>
            </Flex>
          </Column>
        );
      }
      if (field.name === 'funder.name') {
        modifiedEditField = {
          ...field,
          options: createSelectOptions(funderData),
          value: project.funder_id,
          isClearable: false,
          name: 'funder_id',
        };
      }
      if (field.name === 'client.name') {
        modifiedEditField = {
          ...field,
          options: createSelectOptions(clientData),
          value: project.client_id,
          isClearable: false,
          name: 'client_id',
        };
      }
      return (
        <FieldColumn
          key={field.name}
          width={field.width || 300}
          {...(modifiedEditField || field)}
        />
      );
    }

    return (
      <ReadOnlyColumn
        key={field.name}
        data={project}
        width={(field.width || 300) as any}
        field={modifiedReadOnlyField || field}
      />
    );
  });
};

export default GeneralInformationFields;
